import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BankConnectionCard } from "../components/integrations/BankConnectionCard";
import { InvitePartnerModal, PendingActionBanner } from "../components/household";
import { BetaFeedbackPrompt } from "../components/feedback";
import { usePrivacy } from "../components/privacy/PrivacyProvider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import api from "../lib/api";
import { 
  Settings as SettingsIcon,
  Users,
  CreditCard,
  Tag,
  Plus,
  Trash2,
  Edit,
  UserPlus,
  Mail,
  Crown,
  User,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  Bell,
  BellOff,
  Moon,
  Clock,
  Calendar,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Building2,
  Pencil,
  X,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Palette,
  Sun,
  Monitor,
  Check,
  UserCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Privacy Style Settings Component
function PrivacyStyleSettings({ householdId, currentStyle, onUpdate }) {
  const [selectedStyle, setSelectedStyle] = useState(currentStyle || 'fully_shared');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (currentStyle) setSelectedStyle(currentStyle); }, [currentStyle]);

  const styles = [
    {
      id: 'fully_shared',
      name: 'Fully Shared',
      description: 'Everything is visible to both of you — one shared view of all money.',
      icon: <Users className="w-6 h-6 text-emerald-500" />,
      features: ['Full visibility of all accounts', 'Shared transaction history', 'Joint financial decisions']
    },
    {
      id: 'shared_personal',
      name: 'Shared + Personal',
      description: 'See shared finances together, but keep personal spending private.',
      icon: <Shield className="w-6 h-6 text-blue-500" />,
      features: ['Shared bills visibility', 'Personal spending stays private', 'Household totals always accurate']
    },
    {
      id: 'separate_shared_goals',
      name: 'Separate + Shared Goals',
      description: 'Manage money separately, but align around shared goals.',
      icon: <EyeOff className="w-6 h-6 text-purple-500" />,
      features: ['Maximum financial privacy', 'Shared goals remain visible', 'Contributions tracked']
    },
    {
      id: 'solo',
      name: 'Solo Household',
      description: 'Your personal financial system — no partner framing.',
      icon: <UserCircle className="w-6 h-6 text-slate-500" />,
      features: ['Single-user experience', 'No shared language', 'Full personal control']
    }
  ];

  const handleSave = async () => {
    if (selectedStyle === currentStyle) return;
    
    // Confirmation for switching to solo
    if (selectedStyle === 'solo' && currentStyle !== 'solo') {
      const confirmed = window.confirm(
        "This will switch your experience to a personal mode. Shared language and partner-based features will be removed. You can change this anytime."
      );
      if (!confirmed) return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/households/${householdId}/privacy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ privacy_style: selectedStyle })
      });

      if (!response.ok) throw new Error('Failed to update privacy style');
      
      toast.success('Privacy style updated');
      onUpdate?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-dashboard" data-testid="privacy-style-settings">
      <div className="mb-6">
        <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-500" />
          Privacy Style
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Choose how your financial experience works. This can be changed at any time.
        </p>
      </div>

      <div className="grid gap-4">
        {styles.map((style) => (
          <div
            key={style.id}
            onClick={() => setSelectedStyle(style.id)}
            data-testid={`privacy-style-${style.id}`}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              selectedStyle === style.id
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                : 'border-border hover:border-muted-foreground/50'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`p-2 rounded-lg ${
                selectedStyle === style.id ? 'bg-emerald-100 dark:bg-emerald-900' : 'bg-muted'
              }`}>
                {style.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold">{style.name}</h4>
                  {currentStyle === style.id && (
                    <Badge variant="secondary" className="text-xs">Current</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{style.description}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {style.features.map((feature, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-muted rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedStyle === style.id
                  ? 'border-emerald-500 bg-emerald-500'
                  : 'border-muted-foreground/30'
              }`}>
                {selectedStyle === style.id && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedStyle !== currentStyle && (
        <div className="mt-6 flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-900">
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200">Ready to change privacy style?</p>
            <p className="text-sm text-amber-600 dark:text-amber-400">This will change how your financial experience looks and feels.</p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-primary"
            data-testid="save-privacy-style-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Changes
          </Button>
        </div>
      )}

      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Note:</strong> Regardless of privacy style, household totals (net worth, cashflow) 
          always include all accounts to ensure financial accuracy and build trust.
        </p>
      </div>
    </div>
  );
}


// Notification Settings Component
function NotificationSettings({ householdId }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);

  const dayOptions = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const timeOptions = [];
  for (let h = 7; h <= 22; h++) {
    timeOptions.push(`${h.toString().padStart(2, '0')}:00`);
    if (h < 22) timeOptions.push(`${h.toString().padStart(2, '0')}:30`);
  }

  const fetchPrefs = async () => {
    try {
      const [prefsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${householdId}/notifications/preferences`, {
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/households/${householdId}/notifications/push/status`, {
          credentials: 'include'
        })
      ]);
      
      if (prefsRes.ok) {
        const data = await prefsRes.json();
        setPrefs(data);
      }
      
      if (statusRes.ok) {
        const data = await statusRes.json();
        setPushStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch notification prefs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (householdId) fetchPrefs();
  }, [householdId]);

  const updatePref = async (key, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/households/${householdId}/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPrefs(data);
        toast.success('Preference updated');
      } else {
        toast.error('Could not update preference. Please try again.');
      }
    } catch (error) {
      console.error('Preference update error:', error);
      toast.error('Could not update preference. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Push notifications not supported in this browser');
      return;
    }

    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      // In production, register service worker and get subscription
      // For now, simulate subscription
      try {
        const res = await fetch(`${API_URL}/api/households/${householdId}/notifications/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            endpoint: `https://push.example.com/${Date.now()}`,
            keys: { p256dh: 'mock', auth: 'mock' },
            user_agent: navigator.userAgent
          })
        });
        
        if (res.ok) {
          toast.success('Push notifications enabled');
          fetchPrefs();
        }
      } catch (error) {
        toast.error('Could not enable push notifications. Please try again.');
      }
    } else {
      toast.error('Push notification permission denied');
    }
  };

  const disablePush = async () => {
    try {
      await fetch(`${API_URL}/api/households/${householdId}/notifications/push/unsubscribe`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      toast.success('Push notifications disabled');
      fetchPrefs();
    } catch (error) {
      toast.error('Could not disable push notifications. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!prefs) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-muted-foreground">Failed to load notification settings</p>
        <Button variant="outline" onClick={fetchPrefs} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="notification-settings">
      {/* Push Status Banner */}
      <div className={`p-4 rounded-lg border ${prefs.push_enabled 
        ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800' 
        : 'bg-muted/50 border-border'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {prefs.push_enabled ? (
              <Bell className="w-5 h-5 text-emerald-600" />
            ) : (
              <BellOff className="w-5 h-5 text-muted-foreground" />
            )}
            <div>
              <p className="font-medium">
                {prefs.push_enabled ? 'Push notifications enabled' : 'Push notifications disabled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {prefs.push_enabled 
                  ? `${prefs.push_subscription_count} device(s) registered` 
                  : 'Enable to receive gentle reminders'}
              </p>
            </div>
          </div>
          
          {prefs.push_enabled ? (
            <Button variant="outline" onClick={disablePush} size="sm">
              Disable
            </Button>
          ) : (
            <Button onClick={requestPushPermission} size="sm" className="btn-primary">
              Enable Push
            </Button>
          )}
        </div>
        
        {prefs.push_enabled && (
          <div className="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800 flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">This week:</span>
            <span className="font-medium">{prefs.pushes_sent_this_week} of {prefs.max_push_per_week} sent</span>
          </div>
        )}
      </div>

      {/* Financial Calm Mode */}
      <div className="p-4 rounded-lg border bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Moon className="w-5 h-5 text-purple-600" />
            <div>
              <p className="font-medium">Financial Calm Mode</p>
              <p className="text-sm text-muted-foreground">
                Reduces notifications to 1/week. Only weekly check-in reminders.
              </p>
            </div>
          </div>
          <Switch
            checked={prefs.low_stimulation_mode}
            onCheckedChange={(checked) => updatePref('low_stimulation_mode', checked)}
            data-testid="low-stimulation-toggle"
          />
        </div>
      </div>

      {/* Notification Types */}
      <div className="space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Notification Types
        </h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
            <div>
              <p className="font-medium">Weekly Reconcile Reminder</p>
              <p className="text-sm text-muted-foreground">Gentle nudge to review your finances</p>
            </div>
            <Switch
              checked={prefs.weekly_reconcile_enabled}
              onCheckedChange={(checked) => updatePref('weekly_reconcile_enabled', checked)}
              data-testid="reconcile-reminder-toggle"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
            <div>
              <p className="font-medium">Bill Due Reminders</p>
              <p className="text-sm text-muted-foreground">24-48 hours before bills are due</p>
            </div>
            <Switch
              checked={prefs.bill_reminders_enabled}
              onCheckedChange={(checked) => updatePref('bill_reminders_enabled', checked)}
              data-testid="bill-reminder-toggle"
            />
          </div>
          
          <div className={`flex items-center justify-between p-3 rounded-lg border bg-card ${prefs.low_stimulation_mode ? 'opacity-50' : ''}`}>
            <div>
              <p className="font-medium">Discretionary Alerts</p>
              <p className="text-sm text-muted-foreground">When approaching spending targets</p>
              {prefs.low_stimulation_mode && (
                <Badge variant="secondary" className="mt-1 text-xs">Disabled in Calm Mode</Badge>
              )}
            </div>
            <Switch
              checked={prefs.discretionary_alerts_enabled && !prefs.low_stimulation_mode}
              onCheckedChange={(checked) => updatePref('discretionary_alerts_enabled', checked)}
              disabled={prefs.low_stimulation_mode}
              data-testid="discretionary-alert-toggle"
            />
          </div>
          
          <div className={`flex items-center justify-between p-3 rounded-lg border bg-card ${prefs.low_stimulation_mode ? 'opacity-50' : ''}`}>
            <div>
              <p className="font-medium">Streak Rewards</p>
              <p className="text-sm text-muted-foreground">Celebrate consistency milestones</p>
              {prefs.low_stimulation_mode && (
                <Badge variant="secondary" className="mt-1 text-xs">Disabled in Calm Mode</Badge>
              )}
            </div>
            <Switch
              checked={prefs.streak_rewards_enabled && !prefs.low_stimulation_mode}
              onCheckedChange={(checked) => updatePref('streak_rewards_enabled', checked)}
              disabled={prefs.low_stimulation_mode}
              data-testid="streak-reward-toggle"
            />
          </div>
        </div>
      </div>

      {/* Timing Preferences */}
      <div className="space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Timing Preferences
        </h4>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-4 rounded-lg border bg-card">
            <Label className="text-sm font-medium">Preferred Reconcile Day</Label>
            <select
              value={prefs.preferred_reconcile_day}
              onChange={(e) => updatePref('preferred_reconcile_day', e.target.value)}
              className="w-full mt-2 p-2 rounded border bg-background"
              data-testid="reconcile-day-select"
            >
              {dayOptions.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
          
          <div className="p-4 rounded-lg border bg-card">
            <Label className="text-sm font-medium">Preferred Reconcile Time</Label>
            <select
              value={prefs.preferred_reconcile_time}
              onChange={(e) => updatePref('preferred_reconcile_time', e.target.value)}
              className="w-full mt-2 p-2 rounded border bg-background"
              data-testid="reconcile-time-select"
            >
              {timeOptions.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Quiet Hours */}
      <div className="space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <Moon className="w-4 h-4" />
          Quiet Hours
          <Badge variant="secondary" className="text-xs">No notifications during these hours</Badge>
        </h4>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-4 rounded-lg border bg-card">
            <Label className="text-sm font-medium">Start Time</Label>
            <select
              value={prefs.quiet_hours_start}
              onChange={(e) => updatePref('quiet_hours_start', e.target.value)}
              className="w-full mt-2 p-2 rounded border bg-background"
              data-testid="quiet-start-select"
            >
              {Array.from({length: 24}, (_, i) => {
                const time = `${i.toString().padStart(2, '0')}:00`;
                return <option key={time} value={time}>{time}</option>;
              })}
            </select>
          </div>
          
          <div className="p-4 rounded-lg border bg-card">
            <Label className="text-sm font-medium">End Time</Label>
            <select
              value={prefs.quiet_hours_end}
              onChange={(e) => updatePref('quiet_hours_end', e.target.value)}
              className="w-full mt-2 p-2 rounded border bg-background"
              data-testid="quiet-end-select"
            >
              {Array.from({length: 24}, (_, i) => {
                const time = `${i.toString().padStart(2, '0')}:00`;
                return <option key={time} value={time}>{time}</option>;
              })}
            </select>
          </div>
        </div>
        
        <p className="text-sm text-muted-foreground">
          Default: 8 PM - 7 AM. Notifications triggered during quiet hours will be delayed until morning.
        </p>
      </div>

      {/* Frequency Caps */}
      <div className="space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Frequency Limits
        </h4>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-4 rounded-lg border bg-card">
            <Label className="text-sm font-medium">Max Notifications Per Week</Label>
            <select
              value={prefs.max_push_per_week}
              onChange={(e) => updatePref('max_push_per_week', parseInt(e.target.value))}
              className="w-full mt-2 p-2 rounded border bg-background"
              data-testid="max-week-select"
            >
              {[1, 2, 3, 5, 7, 10].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          
          <div className="p-4 rounded-lg border bg-card">
            <Label className="text-sm font-medium">Max Notifications Per Day</Label>
            <select
              value={prefs.max_push_per_day}
              onChange={(e) => updatePref('max_push_per_day', parseInt(e.target.value))}
              className="w-full mt-2 p-2 rounded border bg-background"
              data-testid="max-day-select"
            >
              {[1, 2, 3].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Info Footer */}
      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Privacy note:</strong> Push notifications never include specific transaction details, 
          merchant names, or account balances. Only gentle, actionable reminders.
        </p>
      </div>
    </div>
  );
}

// ========== AppearanceTab Component ==========
const ENVIRONMENTS = [
  {
    id: 'calm',
    name: 'Calm',
    description: 'Soft neutrals and warm beige. Safe, spacious, premium.',
    colors: { primary: '#6E7F80', accent: '#A7B0A8', bg: '#E8E1D6', card: '#F5EFE6', text: '#2F2F2F' },
    darkColors: { primary: '#8DA0A1', accent: '#A7B0A8', bg: '#1A1612', card: '#231E1A', text: '#E8DFD2' },
  },
  {
    id: 'focus',
    name: 'Focus',
    description: 'Controlled and sharp. Built for clarity and decisions.',
    colors: { primary: '#314B6E', accent: '#607EA2', bg: '#edf0f4', card: '#f8f9fb', text: '#151c26' },
    darkColors: { primary: '#314B6E', accent: '#607EA2', bg: '#0E141C', card: '#16202B', text: '#E6EEF6' },
  },
  {
    id: 'energy',
    name: 'Energy',
    description: 'Warm coral and gold tones. Vibrant and motivating.',
    colors: { primary: '#9a3412', accent: '#d97706', bg: '#fdf8f6', card: '#ffffff', text: '#1c1917' },
    darkColors: { primary: '#ea6d47', accent: '#fbbf24', bg: '#1a0e0a', card: '#2a1610', text: '#fce8e1' },
  },
  {
    id: 'wealth',
    name: 'Wealth',
    description: 'Rich gold and deep emerald. Premium, aspirational feel.',
    colors: { primary: '#7a5c2e', accent: '#0d9668', bg: '#fdfaf5', card: '#ffffff', text: '#1c1917' },
    darkColors: { primary: '#c49240', accent: '#34d399', bg: '#1a150a', card: '#2a2010', text: '#f5ecd8' },
  },
  {
    id: 'balance',
    name: 'Balance',
    description: 'Sage teal with soft lavender. Harmonious and zen.',
    colors: { primary: '#2d6a5e', accent: '#7c5cbf', bg: '#f7fbfa', card: '#ffffff', text: '#1c1917' },
    darkColors: { primary: '#5aad9a', accent: '#a78bfa', bg: '#0c1a17', card: '#142b25', text: '#e0f5f0' },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Greyscale with subtle accents. Clean and quiet.',
    colors: { primary: '#3b4252', accent: '#6b7280', bg: '#fcfcfd', card: '#ffffff', text: '#1f2937' },
    darkColors: { primary: '#8494b0', accent: '#8494b0', bg: '#111318', card: '#1a1d24', text: '#e5e7eb' },
  },
  {
    id: 'clarity',
    name: 'Clarity',
    description: 'Clear and decisive. Orange action with teal balance.',
    colors: { primary: '#E07A2F', accent: '#4FA9B3', bg: '#F7F5F2', card: '#FFFFFF', text: '#2A1F2D' },
    darkColors: { primary: '#E07A2F', accent: '#4FA9B3', bg: '#1C1520', card: '#271D2A', text: '#F0EDE8' },
  },
];

const MODE_OPTIONS = [
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'dark', label: 'Dark', icon: Moon },
  { id: 'system', label: 'System', icon: Monitor },
];

function AppearanceTab() {
  const { mode, setMode, resolvedMode, environment, setEnvironment } = useTheme();

  // Session suggestion: show after 3+ sessions if still on Calm
  const [showSuggestion, setShowSuggestion] = useState(false);
  useEffect(() => {
    const count = parseInt(localStorage.getItem('arawealth-session-count') || '0', 10);
    if (count >= 3 && environment === 'calm') {
      setShowSuggestion(true);
    }
  }, [environment]);

  return (
    <div className="space-y-8" data-testid="appearance-tab">
      {/* ── Mode Toggle ── */}
      <div className="card-dashboard">
        <h3 className="font-heading font-semibold mb-1">Mode</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Choose how the interface looks in light or dark environments.
        </p>
        <div className="flex gap-3">
          {MODE_OPTIONS.map(({ id, label, icon: Icon }) => {
            const active = mode === id;
            return (
              <button
                key={id}
                data-testid={`mode-${id}`}
                onClick={() => setMode(id)}
                className={`
                  flex items-center gap-2.5 px-5 py-2.5 rounded-xl border text-sm font-medium
                  transition-all duration-200
                  ${active
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-500 shadow-sm'
                    : 'border-stone-200 dark:border-stone-700 text-muted-foreground hover:border-stone-300 dark:hover:border-stone-600 hover:bg-stone-50 dark:hover:bg-stone-800/30'}
                `}
              >
                <Icon className="w-4 h-4" />
                {label}
                {active && <Check className="w-3.5 h-3.5 ml-1" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Environment ── */}
      <div className="card-dashboard">
        <h3 className="font-heading font-semibold mb-1">Environment</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Pick a colour palette that sets your mood. Everything updates instantly.
        </p>

        {showSuggestion && (
          <div className="mb-5 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="env-suggestion">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
            <span>You've been using <strong>Calm</strong> for a while. Try a different environment and see what feels right.</span>
            <button onClick={() => setShowSuggestion(false)} className="ml-auto shrink-0 text-amber-500 hover:text-amber-700"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ENVIRONMENTS.map((env) => {
            const active = environment === env.id;
            const c = resolvedMode === 'dark' ? env.darkColors : env.colors;
            return (
              <button
                key={env.id}
                data-testid={`env-${env.id}`}
                onClick={() => { setEnvironment(env.id); setShowSuggestion(false); }}
                className={`
                  relative text-left rounded-xl border-2 p-4 group
                  transition-all duration-200
                  ${active
                    ? 'border-emerald-600 dark:border-emerald-400 shadow-md'
                    : 'border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600 hover:shadow-sm'}
                `}
              >
                {active && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-emerald-600 dark:bg-emerald-400 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white dark:text-emerald-950" />
                  </div>
                )}
                {/* Mini preview */}
                <div
                  className="rounded-lg overflow-hidden mb-3 border border-black/5 dark:border-white/5"
                  style={{ background: c.bg }}
                >
                  <div className="flex gap-1.5 p-2">
                    <div className="w-8 rounded-md flex flex-col gap-0.5 p-1" style={{ background: c.primary }}>
                      <div className="h-1 rounded-full opacity-60" style={{ background: c.accent }} />
                      <div className="h-1 rounded-full opacity-30" style={{ background: '#fff' }} />
                      <div className="h-1 rounded-full opacity-30" style={{ background: '#fff' }} />
                    </div>
                    <div className="flex-1 flex flex-col gap-1">
                      <div className="flex gap-1">
                        <div className="flex-1 h-6 rounded" style={{ background: c.card, border: `1px solid ${c.primary}22` }}>
                          <div className="h-1 w-6 rounded-full m-1" style={{ background: c.primary, opacity: 0.6 }} />
                          <div className="h-0.5 w-8 rounded-full mx-1" style={{ background: c.text, opacity: 0.15 }} />
                        </div>
                        <div className="flex-1 h-6 rounded" style={{ background: c.card, border: `1px solid ${c.primary}22` }}>
                          <div className="h-1 w-5 rounded-full m-1" style={{ background: c.accent, opacity: 0.8 }} />
                          <div className="h-0.5 w-7 rounded-full mx-1" style={{ background: c.text, opacity: 0.15 }} />
                        </div>
                      </div>
                      <div className="h-4 rounded flex items-center px-1" style={{ background: c.card, border: `1px solid ${c.primary}22` }}>
                        <div className="h-1.5 flex-1 rounded-full" style={{ background: c.primary, opacity: 0.2 }}>
                          <div className="h-full rounded-full" style={{ background: c.primary, width: '55%' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <h4 className="font-heading font-semibold text-sm">{env.name}</h4>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{env.description}</p>
                <div className="flex gap-1.5 mt-2">
                  {[c.primary, c.accent, c.bg, c.card, c.text].map((color, i) => (
                    <div
                      key={i}
                      className="w-3.5 h-3.5 rounded-full border border-black/10 dark:border-white/10"
                      style={{ background: color }}
                    />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ========== AccountsTab Component ==========
function AccountsTab({ household, accounts, setAccounts, showAccountDialog, setShowAccountDialog, newAccount, setNewAccount, handleAddAccount, fetchData, formatCurrency, refreshAccounts }) {
  const [editMode, setEditMode] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenAccounts, setHiddenAccounts] = useState([]);

  const fetchHidden = useCallback(async () => {
    if (!household?.household_id) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/accounts?include_hidden=true`, { credentials: "include" });
      if (res.ok) {
        const all = await res.json();
        setHiddenAccounts(all.filter(a => a.is_hidden));
      }
    } catch {}
  }, [household?.household_id]);

  useEffect(() => { if (showHidden) fetchHidden(); }, [showHidden, fetchHidden]);

  const getDisplayName = (acc) => acc.display_name || acc.name;
  const getOriginalName = (acc) => acc.original_name || (acc.display_name && acc.display_name !== acc.name ? acc.name : null);

  const PURPOSE_PRESETS = ["Spending", "Bills", "Savings", "Emergency", "Kids", "Business"];

  const handleSetPurpose = async (acc, purpose) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/accounts/${acc.account_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ purpose: purpose || null }),
      });
      if (!res.ok) throw new Error("Update failed");
      toast.success(purpose ? `Labeled as "${purpose}"` : "Label removed");
      fetchData();
      refreshAccounts();
    } catch (e) { toast.error(e.message); }
  };
  const isManual = (acc) => {
    // An account is manual only if it has no bank connection
    if (acc.connection_id && (acc.connection_id.startsWith("akahu_") || acc.connection_id.startsWith("plaid_"))) return false;
    if (acc.provider && acc.provider !== "manual") return false;
    return true;
  };

  const handleRename = async (acc) => {
    if (!renameValue.trim()) { toast.error("Enter a name"); return; }
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/accounts/${acc.account_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ display_name: renameValue.trim() }),
      });
      if (!res.ok) throw new Error("Rename failed");
      toast.success("Account renamed");
      setRenaming(null);
      fetchData();
      refreshAccounts();
    } catch (e) { toast.error(e.message); }
  };

  const handleHide = async (acc) => {
    if (!window.confirm(`Hide "${getDisplayName(acc)}"? It won't appear in your account lists, but historical transactions are preserved.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/accounts/${acc.account_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ is_hidden: true }),
      });
      if (!res.ok) throw new Error("Hide failed");
      toast.success("Account hidden");
      fetchData();
      fetchHidden();
      refreshAccounts();
    } catch (e) { toast.error(e.message); }
  };

  const handleUnhide = async (acc) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/accounts/${acc.account_id}/unhide`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Unhide failed");
      toast.success("Account restored");
      fetchData();
      fetchHidden();
      refreshAccounts();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`Permanently delete "${getDisplayName(acc)}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/accounts/${acc.account_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Delete failed");
      }
      toast.success("Account deleted");
      fetchData();
      refreshAccounts();
    } catch (e) { toast.error(e.message); }
  };

  const handleMove = async (index, direction) => {
    const newOrder = [...accounts];
    const targetIdx = direction === "up" ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= newOrder.length) return;
    [newOrder[index], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[index]];
    setAccounts(newOrder);

    try {
      const body = { accounts: newOrder.map((a, i) => ({ account_id: a.account_id, sort_order: i })) };
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/accounts/reorder`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Reorder failed");
      refreshAccounts();
    } catch (e) {
      toast.error(e.message);
      fetchData();
      refreshAccounts();
    }
  };

  return (
    <div className="space-y-6" data-testid="accounts-tab-content">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {editMode ? (
            <Button onClick={() => setEditMode(false)} variant="outline" size="sm" className="rounded-full" data-testid="done-editing-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Done editing
            </Button>
          ) : (
            <Button onClick={() => setEditMode(true)} variant="ghost" size="sm" className="text-muted-foreground" data-testid="edit-accounts-btn">
              <Pencil className="w-4 h-4 mr-1.5" />Edit accounts
            </Button>
          )}
        </div>
        <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-account-btn">
              <Plus className="w-4 h-4 mr-2" />Add Account
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby="add-acc-desc">
            <DialogHeader><DialogTitle>Add Account</DialogTitle></DialogHeader>
            <p id="add-acc-desc" className="sr-only">Add a manual account</p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Account Name *</Label>
                <Input value={newAccount.name} onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })} placeholder="e.g., ANZ Everyday" data-testid="account-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select value={newAccount.account_type} onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3">
                    <option value="checking">Checking</option><option value="savings">Savings</option><option value="credit">Credit Card</option><option value="loan">Loan</option><option value="investment">Investment</option><option value="cash">Cash</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Privacy</Label>
                  <select value={newAccount.privacy_level} onChange={(e) => setNewAccount({ ...newAccount, privacy_level: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3">
                    <option value="shared">Shared</option><option value="totals_only">Totals Only</option><option value="private">Private</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Institution</Label>
                  <Input value={newAccount.institution} onChange={(e) => setNewAccount({ ...newAccount, institution: e.target.value })} placeholder="e.g., ANZ" />
                </div>
                <div className="space-y-2">
                  <Label>Current Balance</Label>
                  <Input type="number" step="0.01" value={newAccount.balance} onChange={(e) => setNewAccount({ ...newAccount, balance: e.target.value })} placeholder="0.00" data-testid="account-balance-input" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAccountDialog(false)}>Cancel</Button>
                <Button onClick={handleAddAccount} className="btn-primary" data-testid="save-account-btn">Add Account</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active Accounts */}
      <div className="card-dashboard">
        <h3 className="font-heading font-semibold mb-4">Your Accounts</h3>
        {accounts.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
            <CreditCard className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium mb-1">No accounts</p>
            <p className="text-xs text-muted-foreground mb-3">Add a manual account or connect your bank.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map((account, idx) => (
              <div
                key={account.account_id}
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                data-testid={`account-${account.account_id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {editMode && (
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => handleMove(idx, "up")} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20" data-testid={`move-up-${account.account_id}`}>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleMove(idx, "down")} disabled={idx === accounts.length - 1} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20" data-testid={`move-down-${account.account_id}`}>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    account.account_type === "credit" || account.account_type === "loan" ? "bg-red-100 dark:bg-red-900/30" : "bg-emerald-100 dark:bg-emerald-900/30"
                  }`}>
                    <CreditCard className={`w-5 h-5 ${account.account_type === "credit" || account.account_type === "loan" ? "text-red-600" : "text-emerald-600"}`} />
                  </div>
                  <div className="min-w-0">
                    {renaming === account.account_id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleRename(account); if (e.key === "Escape") setRenaming(null); }}
                          className="h-8 text-sm w-48"
                          autoFocus
                          data-testid={`rename-input-${account.account_id}`}
                        />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleRename(account)}>
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setRenaming(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className="font-medium truncate">{getDisplayName(account)}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="capitalize">{account.account_type}</span>
                          {account.institution && <><span className="text-xs">•</span><span>{account.institution}</span></>}
                          {getOriginalName(account) && (
                            <span className="text-xs text-slate-400 italic ml-1">({getOriginalName(account)})</span>
                          )}
                          {account.purpose && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300" data-testid={`purpose-tag-${account.account_id}`}>
                              {account.purpose}
                            </span>
                          )}
                        </div>
                        {editMode && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <select
                              value={account.purpose || ""}
                              onChange={(e) => handleSetPurpose(account, e.target.value)}
                              className="h-6 text-xs rounded border border-slate-200 dark:border-slate-700 bg-transparent px-1.5 text-slate-500"
                              data-testid={`purpose-select-${account.account_id}`}
                            >
                              <option value="">No label</option>
                              {PURPOSE_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {account.privacy_level === "private" && <EyeOff className="w-4 h-4 text-muted-foreground" />}
                  <span className={`font-medium tabular-nums ${account.balance < 0 ? "text-destructive" : ""}`}>
                    {formatCurrency(account.balance)}
                  </span>
                  {editMode && (
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => { setRenaming(account.account_id); setRenameValue(getDisplayName(account)); }}
                        data-testid={`rename-btn-${account.account_id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => handleHide(account)}
                        data-testid={`hide-btn-${account.account_id}`}
                      >
                        <EyeOff className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                      {isManual(account) && (
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(account)}
                          data-testid={`delete-btn-${account.account_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden Accounts */}
      <div className="card-dashboard">
        <button
          onClick={() => setShowHidden(!showHidden)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="toggle-hidden-btn"
        >
          {showHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          {showHidden ? "Hide" : "Show"} hidden accounts
          {hiddenAccounts.length > 0 && !showHidden && (
            <span className="text-xs bg-muted px-2 py-0.5 rounded-full">{hiddenAccounts.length}</span>
          )}
        </button>

        {showHidden && hiddenAccounts.length > 0 && (
          <div className="mt-4 space-y-2">
            {hiddenAccounts.map(acc => (
              <div key={acc.account_id} className="flex items-center justify-between p-3 rounded-lg border border-dashed border-border opacity-60 hover:opacity-100 transition-opacity" data-testid={`hidden-account-${acc.account_id}`}>
                <div className="flex items-center gap-3">
                  <EyeOff className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{getDisplayName(acc)}</p>
                    <p className="text-xs text-muted-foreground capitalize">{acc.account_type} {acc.institution ? `• ${acc.institution}` : ""}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUnhide(acc)} data-testid={`unhide-btn-${acc.account_id}`}>
                  Restore
                </Button>
              </div>
            ))}
          </div>
        )}
        {showHidden && hiddenAccounts.length === 0 && (
          <p className="mt-3 text-xs text-muted-foreground">No hidden accounts</p>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { household, user, checkAuth, refreshAccounts } = useAuth();
  const navigate = useNavigate();
  const { isSolo } = usePrivacy();
  const [loading, setLoading] = useState(true);
  const [householdData, setHouseholdData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Form states
  const [newAccount, setNewAccount] = useState({
    name: "",
    account_type: "checking",
    institution: "",
    privacy_level: "shared",
    balance: ""
  });

  const [newCategory, setNewCategory] = useState({
    name: "",
    group: "variable",
    privacy_allowed: false
  });

  const [newRule, setNewRule] = useState({
    field: "description",
    operator: "contains",
    value: "",
    category_id: ""
  });

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("partner");
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    if (!household) return;
    fetchData();
  }, [household]);

  const fetchData = async () => {
    try {
      const [hhRes, accRes, catRes, rulesRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/accounts`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/categories`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/rules`, { credentials: 'include' })
      ]);

      if (hhRes.ok) setHouseholdData(await hhRes.json());
      if (accRes.ok) setAccounts(await accRes.json());
      if (catRes.ok) setCategories(await catRes.json());
      if (rulesRes.ok) setRules(await rulesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.name) {
      toast.error("Please enter account name");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newAccount,
          balance: parseFloat(newAccount.balance) || 0
        })
      });

      if (!response.ok) throw new Error('Failed to add account');

      toast.success("Account added");
      setShowAccountDialog(false);
      setNewAccount({ name: "", account_type: "checking", institution: "", privacy_level: "shared", balance: "" });
      fetchData();
      refreshAccounts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const [editingCategory, setEditingCategory] = useState(null);
  const [editCatForm, setEditCatForm] = useState({ name: '', group: '' });

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error("Please enter category name");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newCategory)
      });

      if (response.status === 409) {
        toast.error("Category already exists");
        return;
      }
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to add category');
      }

      toast.success("Category added");
      setShowCategoryDialog(false);
      setNewCategory({ name: "", group: "variable", privacy_allowed: false });
      fetchData();
    } catch (error) {
      console.error("Add category error:", error);
      toast.error(error.message);
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory || !editCatForm.name.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/categories/${editingCategory.category_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editCatForm.name.trim(), group: editCatForm.group })
      });
      if (res.status === 409) { toast.error("Category already exists"); return; }
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
      toast.success("Category updated");
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      console.error("Edit category error:", error);
      toast.error(error.message);
    }
  };

  const handleDeleteCategory = async (catId, catName) => {
    if (!window.confirm(`Delete "${catName}"? This will unlink it from any allocations.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/categories/${catId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      console.error("Delete category error:", error);
      toast.error(error.message);
    }
  };

  const handleAddRule = async () => {
    if (!newRule.value || !newRule.category_id) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          priority: rules.length + 1,
          match_logic: {
            field: newRule.field,
            operator: newRule.operator,
            value: newRule.value
          },
          action: {
            set_category: newRule.category_id
          }
        })
      });

      if (!response.ok) throw new Error('Failed to add rule');

      toast.success("Rule added");
      setShowRuleDialog(false);
      setNewRule({ field: "description", operator: "contains", value: "", category_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/rules/${ruleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success("Rule deleted");
      fetchData();
    } catch (error) {
      toast.error("Could not delete rule. Please try again.");
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error("Please enter email");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole })
      });

      if (!response.ok) throw new Error('Failed to send invite');

      toast.success("Invite sent");
      setShowInviteDialog(false);
      setInviteEmail("");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(amount);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-amber-500" />;
      case 'partner': return <Users className="w-4 h-4 text-emerald-500" />;
      case 'teen': return <User className="w-4 h-4 text-blue-500" />;
      case 'viewer': return <Eye className="w-4 h-4 text-stone-500" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 lg:p-8" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Manage your household, accounts, and preferences</p>
        </div>

        <Tabs defaultValue="household" className="space-y-6">
          <TabsList>
            <TabsTrigger value="household" data-testid="tab-household">
              <Users className="w-4 h-4 mr-2" />
              Household
            </TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts">
              <CreditCard className="w-4 h-4 mr-2" />
              Accounts
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">
              <Building2 className="w-4 h-4 mr-2" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              <Tag className="w-4 h-4 mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules">
              <SettingsIcon className="w-4 h-4 mr-2" />
              Rules
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="appearance" data-testid="tab-appearance">
              <Palette className="w-4 h-4 mr-2" />
              Appearance
            </TabsTrigger>
          </TabsList>

          {/* Household Tab */}
          <TabsContent value="household" className="space-y-6">
            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-4">Household Details</h3>
              
              <div className="grid gap-4">
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">{householdData?.name}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Currency</span>
                  <span className="font-medium">{householdData?.currency}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Privacy Style</span>
                  <Badge variant="secondary" className="capitalize">
                    {householdData?.style?.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-muted-foreground">Your Role</span>
                  <div className="flex items-center gap-2">
                    {getRoleIcon(householdData?.current_user_role)}
                    <span className="font-medium capitalize">{householdData?.current_user_role}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Privacy Style Settings */}
            <PrivacyStyleSettings 
              householdId={household?.household_id}
              currentStyle={householdData?.privacy_style || householdData?.style || 'fully_shared'}
              onUpdate={fetchData}
            />

            {/* Members — hidden for solo users */}
            {!isSolo && (
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold">Members</h3>
                
                {/* Use new InvitePartnerModal */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowInviteDialog(true)}
                  data-testid="invite-member-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Partner
                </Button>
              </div>
              
              {/* Pending actions banner for creator */}
              <PendingActionBanner 
                householdId={household?.household_id}
                isCreator={householdData?.my_role === "owner"}
                onActionResolved={fetchData}
                className="mb-4"
              />

              <div className="space-y-3">
                {(householdData?.members || []).map((member) => (
                  <div 
                    key={member.user_id}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/30"
                    data-testid={`member-${member.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                        <span className="font-medium text-emerald-700">
                          {member.user_name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{member.user_name}</p>
                        <p className="text-sm text-muted-foreground">{member.user_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {getRoleIcon(member.role)}
                      <Badge variant="secondary" className="capitalize">{member.role}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </TabsContent>

          {/* Accounts Tab */}
          <TabsContent value="accounts" className="space-y-6">
            <AccountsTab 
              household={household}
              accounts={accounts}
              setAccounts={setAccounts}
              showAccountDialog={showAccountDialog}
              setShowAccountDialog={setShowAccountDialog}
              newAccount={newAccount}
              setNewAccount={setNewAccount}
              handleAddAccount={handleAddAccount}
              fetchData={fetchData}
              formatCurrency={formatCurrency}
              refreshAccounts={refreshAccounts}
            />
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-6" data-testid="integrations-tab-content">
            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-4">Bank Connection</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Connect your bank to automatically import transactions.
              </p>
              
              <BankConnectionCard 
                householdId={household?.household_id}
                showCsvAlternative={true}
                onCsvImport={() => navigate("/integrations")}
              />
            </div>
            
            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-4">CSV Import</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Upload transaction files from your bank.
              </p>
              
              <Button 
                variant="outline"
                onClick={() => navigate("/integrations")}
                data-testid="go-to-csv-import"
              >
                Go to Import Page
              </Button>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            <div className="flex justify-end">
              <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Category</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category Name *</Label>
                      <Input
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                        placeholder="e.g., Coffee"
                        data-testid="category-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Group</Label>
                      <select
                        value={newCategory.group}
                        onChange={(e) => setNewCategory({ ...newCategory, group: e.target.value })}
                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                      >
                        <option value="income">Income</option>
                        <option value="fixed">Fixed Expenses</option>
                        <option value="variable">Variable Expenses</option>
                        <option value="debt">Debt</option>
                        <option value="savings">Savings</option>
                        <option value="investing">Investing</option>
                      </select>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
                      <div>
                        <Label>Allow Privacy</Label>
                        <p className="text-xs text-muted-foreground">Can be marked as personal/private</p>
                      </div>
                      <Switch
                        checked={newCategory.privacy_allowed}
                        onCheckedChange={(checked) => setNewCategory({ ...newCategory, privacy_allowed: checked })}
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
                      <Button onClick={handleAddCategory} className="btn-primary" data-testid="save-category-btn">
                        Add Category
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-4">Categories</h3>
              
              <div className="grid gap-2">
                {['income', 'fixed', 'variable', 'debt', 'savings', 'investing'].map((group) => {
                  const groupCategories = categories.filter(c => c.group === group);
                  if (groupCategories.length === 0) return null;
                  
                  return (
                    <div key={group} className="mb-4">
                      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2 capitalize">
                        {group === 'fixed' ? 'Fixed Expenses' : group === 'variable' ? 'Variable Expenses' : group}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {groupCategories.map((cat) => (
                          <div 
                            key={cat.category_id} 
                            className="group inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm border bg-card hover:bg-muted transition-colors"
                            style={{ 
                              backgroundColor: cat.color ? `${cat.color}15` : undefined,
                              borderColor: cat.color || undefined
                            }}
                          >
                            <span>{cat.name}</span>
                            <button
                              onClick={() => { setEditingCategory(cat); setEditCatForm({ name: cat.name, group: cat.group || group }); }}
                              className="ml-0.5 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-200 dark:hover:bg-slate-700 transition-opacity"
                              title="Edit"
                              data-testid={`edit-cat-${cat.category_id}`}
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(cat.category_id, cat.name)}
                              className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 transition-opacity"
                              title="Delete"
                              data-testid={`delete-cat-${cat.category_id}`}
                            >
                              <X className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Edit Category Dialog */}
            <Dialog open={!!editingCategory} onOpenChange={(open) => { if (!open) setEditingCategory(null); }}>
              <DialogContent aria-describedby="edit-cat-desc">
                <DialogHeader>
                  <DialogTitle>Edit Category</DialogTitle>
                </DialogHeader>
                <p id="edit-cat-desc" className="sr-only">Edit category name and group</p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category Name</Label>
                    <Input
                      value={editCatForm.name}
                      onChange={(e) => setEditCatForm({ ...editCatForm, name: e.target.value })}
                      data-testid="edit-category-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Group</Label>
                    <select
                      value={editCatForm.group}
                      onChange={(e) => setEditCatForm({ ...editCatForm, group: e.target.value })}
                      className="w-full h-10 rounded-md border border-input bg-background px-3"
                    >
                      <option value="income">Income</option>
                      <option value="fixed">Fixed Expenses</option>
                      <option value="variable">Variable Expenses</option>
                      <option value="debt">Debt</option>
                      <option value="savings">Savings</option>
                      <option value="investing">Investing</option>
                    </select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setEditingCategory(null)}>Cancel</Button>
                    <Button onClick={handleEditCategory} className="btn-primary" data-testid="save-edit-category-btn">
                      Save
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Rules Tab */}
          <TabsContent value="rules" className="space-y-6">
            <div className="flex justify-end">
              <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-rule-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Rule
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Categorization Rule</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Create a rule to automatically categorize transactions based on their content.
                    </p>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>Field</Label>
                        <select
                          value={newRule.field}
                          onChange={(e) => setNewRule({ ...newRule, field: e.target.value })}
                          className="w-full h-10 rounded-md border border-input bg-background px-3"
                        >
                          <option value="description">Description</option>
                          <option value="merchant">Merchant</option>
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Operator</Label>
                        <select
                          value={newRule.operator}
                          onChange={(e) => setNewRule({ ...newRule, operator: e.target.value })}
                          className="w-full h-10 rounded-md border border-input bg-background px-3"
                        >
                          <option value="contains">Contains</option>
                          <option value="equals">Equals</option>
                          <option value="starts_with">Starts with</option>
                          <option value="ends_with">Ends with</option>
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Value</Label>
                        <Input
                          value={newRule.value}
                          onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                          placeholder="e.g., Countdown"
                          data-testid="rule-value-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Assign Category</Label>
                      <select
                        value={newRule.category_id}
                        onChange={(e) => setNewRule({ ...newRule, category_id: e.target.value })}
                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                        data-testid="rule-category-select"
                      >
                        <option value="">Select category</option>
                        {categories.map(cat => (
                          <option key={cat.category_id} value={cat.category_id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setShowRuleDialog(false)}>Cancel</Button>
                      <Button onClick={handleAddRule} className="btn-primary" data-testid="save-rule-btn">
                        Add Rule
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-4">Auto-Categorization Rules</h3>
              
              {rules.length === 0 ? (
                <div className="text-center py-8">
                  <SettingsIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No rules yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Create rules to auto-categorize transactions</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {rules.map((rule, index) => {
                    const category = categories.find(c => c.category_id === rule.action?.set_category);
                    return (
                      <div 
                        key={rule.rule_id}
                        className="flex items-center justify-between p-4 rounded-lg border border-border"
                        data-testid={`rule-${rule.rule_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">
                              If <span className="text-emerald-600">{rule.match_logic?.field}</span>
                              {' '}<span className="text-muted-foreground">{rule.match_logic?.operator}</span>{' '}
                              "<span className="text-emerald-600">{rule.match_logic?.value}</span>"
                            </p>
                            <p className="text-sm text-muted-foreground">
                              → Set category to <span className="font-medium">{category?.name || 'Unknown'}</span>
                            </p>
                          </div>
                        </div>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-rule-${rule.rule_id}`}>
                              <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Rule?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove the auto-categorization rule. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteRule(rule.rule_id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-4">Notification Preferences</h3>
              <p className="text-muted-foreground mb-6">
                Control how and when you receive gentle reminders about your finances.
              </p>
              
              <NotificationSettings householdId={household?.household_id} />
            </div>
          </TabsContent>

          <TabsContent value="appearance" className="space-y-6">
            <AppearanceTab />
          </TabsContent>
        </Tabs>
        
        {/* Partner Invite Modal */}
        <InvitePartnerModal
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          onInviteSent={() => {
            fetchData();
            setShowFeedback(true);
          }}
        />
        
        {/* Beta Feedback Prompt - appears after invite sent */}
        {showFeedback && (
          <div className="fixed bottom-6 right-6 z-50 max-w-sm" data-testid="household-feedback-prompt">
            <BetaFeedbackPrompt 
              pageKey="household" 
              actionKey="invite_sent"
              onClose={() => setShowFeedback(false)}
            />
          </div>
        )}
      </div>
    </AppLayout>
  );
}
